import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/axios";

/* ============================
   FETCH ALL VENDORS
============================ */
export const fetchVendors = createAsyncThunk(
  "vendormanagement/fetchVendors",
  async ({ page = 1, limit = 10, search = "", filter = "all" }, thunkAPI) => {
    try {
      const res = await api.get(
        `/api/vendormanagement?page=${page}&limit=${limit}&search=${search}&filter=${filter}`
      );
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch vendors");
    }
  }
);

/* ============================
   CREATE VENDOR
============================ */
export const createVendor = createAsyncThunk(
  "vendormanagement/createVendor",
  async (formData, thunkAPI) => {
    try {
      const res = await api.post(`/api/vendormanagement`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to invite vendor"
      );
    }
  }
);

/* ============================
   UPDATE VENDOR
============================ */
export const updateVendor = createAsyncThunk(
  "vendormanagement/updateVendor",
  async ({ id, formData }, thunkAPI) => {
    try {
      const res = await api.put(`/api/vendormanagement/${id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to update vendor"
      );
    }
  }
);

/* ============================
   TOGGLE APPROVAL
============================ */
export const toggleVendorApproval = createAsyncThunk(
  "vendormanagement/toggleApproval",
  async (id, thunkAPI) => {
    try {
      const res = await api.patch(`/api/vendormanagement/${id}/approval`);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to update approval");
    }
  }
);

/* ============================
   TOGGLE STATUS (Suspend/Activate)
============================ */
export const toggleVendorStatus = createAsyncThunk(
  "vendormanagement/toggleStatus",
  async (id, thunkAPI) => {
    try {
      const res = await api.patch(`/api/vendormanagement/${id}/status`);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to update status");
    }
  }
);

/* ============================
   TOGGLE FEATURED VENDOR
============================ */
export const toggleVendorFeatured = createAsyncThunk(
  "vendormanagement/toggleFeatured",
  async (id, thunkAPI) => {
    try {
      const res = await api.patch(`/api/vendormanagement/${id}/featured`);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to update featured status");
    }
  }
);

/* ============================
   TOGGLE BEST SELLER VENDOR
============================ */
export const toggleVendorBestSeller = createAsyncThunk(
  "vendormanagement/toggleBestSeller",
  async (id, thunkAPI) => {
    try {
      const res = await api.patch(`/api/vendormanagement/${id}/bestseller`);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to update best seller status");
    }
  }
);

/* ============================
   DELETE VENDOR
============================ */
export const deleteVendor = createAsyncThunk(
  "vendormanagement/delete",
  async (id, thunkAPI) => {
    try {
      await api.delete(`/api/vendormanagement/${id}`);
      return id;
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Delete failed"
      );
    }
  }
);


export const fetchVendorDetail = createAsyncThunk(
  "vendormanagement/fetchVendorDetail",
  async (id, thunkAPI) => {
    try {
      const res = await api.get(`/api/vendormanagement/${id}/detail`);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to fetch vendor detail"
      );
    }
  }
);

/* ============================
   SLICE
============================ */
const vendorSlice = createSlice({
  name: "vendor",
initialState: {
  vendors: [],
  loading: false,
  error: null,
  pagination: {},
  vendorDetail: null,
  detailLoading: false,
  detailError: null,
},
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchVendors.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVendors.fulfilled, (state, action) => {
        state.loading = false;
        state.vendors = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchVendors.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create
      .addCase(createVendor.fulfilled, (state, action) => {
        state.vendors.unshift(action.payload);
        state.pagination.total = (state.pagination.total || 0) + 1;
      })
      .addCase(createVendor.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Update
      .addCase(updateVendor.fulfilled, (state, action) => {
        const index = state.vendors.findIndex((v) => v._id === action.payload._id);
        if (index !== -1) state.vendors[index] = action.payload;
      })
      .addCase(updateVendor.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Toggle Approval
      .addCase(toggleVendorApproval.fulfilled, (state, action) => {
        const index = state.vendors.findIndex((v) => v._id === action.payload._id);
        if (index !== -1) state.vendors[index] = action.payload;
      })

      // Toggle Status
      .addCase(toggleVendorStatus.fulfilled, (state, action) => {
        const index = state.vendors.findIndex((v) => v._id === action.payload._id);
        if (index !== -1) state.vendors[index] = action.payload;
      })

      // Toggle Featured
      .addCase(toggleVendorFeatured.fulfilled, (state, action) => {
        const index = state.vendors.findIndex((v) => v._id === action.payload._id);
        if (index !== -1) state.vendors[index] = action.payload;
      })

      // Toggle Best Seller
      .addCase(toggleVendorBestSeller.fulfilled, (state, action) => {
        const index = state.vendors.findIndex((v) => v._id === action.payload._id);
        if (index !== -1) state.vendors[index] = action.payload;
      })

      // Delete
      .addCase(deleteVendor.fulfilled, (state, action) => {
        state.vendors = state.vendors.filter((v) => v._id !== action.payload);
        state.pagination.total = (state.pagination.total || 1) - 1;
      })
      .addCase(deleteVendor.rejected, (state, action) => {
        state.error = action.payload;
      })

.addCase(fetchVendorDetail.pending, (state) => {
  state.detailLoading = true;
  state.detailError = null;
})
.addCase(fetchVendorDetail.fulfilled, (state, action) => {
  state.detailLoading = false;
  state.vendorDetail = action.payload;
})
.addCase(fetchVendorDetail.rejected, (state, action) => {
  state.detailLoading = false;
  state.detailError = action.payload;
});
  },
});

export default vendorSlice.reducer;